import { useState, useEffect, useCallback, useRef } from 'react';
import { previewPlayerService } from '../services/PreviewPlayerService';

/**
 * Custom hook to manage global muted state synchronization
 *
 * This hook provides a way for components to subscribe to global muted state changes
 * using the event listener system to avoid conflicts between multiple components.
 *
 * Optimized to prevent unnecessary re-renders by using useCallback and useRef.
 */
export const useGlobalMutedState = () => {
  const [isMuted, setIsMuted] = useState(() => previewPlayerService.getMuted());
  const listenerRef = useRef(null);
  const isSubscribedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple subscriptions
    if (isSubscribedRef.current) return;

    // Function to update local state when global state changes
    const handleMutedChange = muted => {
      setIsMuted(prevMuted => {
        // Only update if the value actually changed
        if (prevMuted !== muted) {
          return muted;
        }
        return prevMuted;
      });
    };

    // Store the listener reference for cleanup
    listenerRef.current = handleMutedChange;
    isSubscribedRef.current = true;

    // Add this component as a listener
    previewPlayerService.addMutedChangeListener(handleMutedChange);

    // Cleanup function
    return () => {
      if (listenerRef.current && isSubscribedRef.current) {
        previewPlayerService.removeMutedChangeListener(listenerRef.current);
        isSubscribedRef.current = false;
      }
    };
  }, []);

  // Function to toggle muted state - memoized to prevent unnecessary re-renders
  const toggleMuted = useCallback(() => {
    return previewPlayerService.toggleMuted();
  }, []);

  return { isMuted, toggleMuted };
};
